// Overview — KPIs (active members by tier, MRR, visits, pending applications) + work queues
// (failed syncs, reconciliation drift) + recent audit activity. Operational metrics only.
import { Link } from "react-router-dom";
import { Surface } from "@championcare/ui";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { formatDateTime, formatMoney } from "../lib/format";
import { Loading, ErrorState, PageHeader, KpiCard, StatusBadge } from "../components/ui";

export function Overview() {
  const api = useApi();
  const kpis = useAsync(() => api.kpis(), []);
  const audit = useAsync(() => api.audit(8), []);

  if (kpis.loading) return <Loading />;
  if (kpis.error) return <ErrorState message={kpis.error} />;
  const k = kpis.data!;

  return (
    <div className="page">
      <PageHeader title="Overview" sub="Operational health at a glance." />

      <div className="kpis">
        <KpiCard label="Active members" value={k.active_members} />
        <KpiCard label="MRR" value={formatMoney(k.mrr_cents)} />
        <KpiCard label="Upcoming visits" value={k.visits.upcoming} />
        <KpiCard
          label="Pending applications"
          value={k.pending_applications}
          tone={k.pending_applications ? "amber" : undefined}
        />
      </div>

      <div className="cols-2">
        <Surface className="card">
          <h2 className="card__title">Members by tier</h2>
          <div className="tier-bars">
            <TierBar label="Basic" n={k.members_by_tier.basic} total={k.active_members} />
            <TierBar label="Premium" n={k.members_by_tier.premium} total={k.active_members} />
            <TierBar label="Fighter" n={k.members_by_tier.fighter} total={k.active_members} gold />
          </div>
        </Surface>

        <Surface className="card">
          <h2 className="card__title">Work queues</h2>
          <Link to="/emr" className="queue-row">
            <span>Failed EMR/Zoom syncs</span>
            <span className={`queue-count ${k.failed_sync ? "is-alert" : ""}`}>
              {k.failed_sync}
            </span>
          </Link>
          <Link to="/emr" className="queue-row">
            <span>Reconciliation drift</span>
            <span className={`queue-count ${k.open_drift ? "is-alert" : ""}`}>{k.open_drift}</span>
          </Link>
          <Link to="/applications" className="queue-row">
            <span>Applications to review</span>
            <span className={`queue-count ${k.pending_applications ? "is-alert" : ""}`}>
              {k.pending_applications}
            </span>
          </Link>
        </Surface>
      </div>

      <Surface className="card">
        <h2 className="card__title">Recent access activity</h2>
        {audit.loading ? (
          <Loading />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>When</th>
                <th>Role</th>
                <th>Action</th>
                <th>Resource</th>
                <th>PHI class</th>
              </tr>
            </thead>
            <tbody>
              {(audit.data ?? []).map((a) => (
                <tr key={String(a.id)}>
                  <td className="mono">{formatDateTime(a.at)}</td>
                  <td>{a.actor_role ?? "—"}</td>
                  <td>{a.action}</td>
                  <td>{a.entity}</td>
                  <td>
                    <StatusBadge value={a.phi_class ?? "NONE"} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Surface>
    </div>
  );
}

function TierBar({
  label,
  n,
  total,
  gold,
}: {
  label: string;
  n: number;
  total: number;
  gold?: boolean;
}) {
  const pct = total ? Math.round((n / total) * 100) : 0;
  return (
    <div className="tier-bar">
      <div className="tier-bar__head">
        <span>{label}</span>
        <span className="mono">{n}</span>
      </div>
      <div className="tier-bar__track">
        <div className={`tier-bar__fill ${gold ? "is-gold" : ""}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
